import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/app/_utils/cn";
import { dayjsTz } from "@/app/_utils/dayjs";
import { formatShowLabel } from "@/app/_utils/showLabel";
import { deriveShowStatus, SHOW_STATUS_DOT_CLASSES, ShowStatus } from "./showStatus";
import StatusBadge from "./StatusBadge";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import ShowMonthNav from "./ShowMonthNav";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/admin/_common/ui/tooltip";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_PILLS_PER_DAY = 3;

const PILL_CLASSES = "bg-card text-foreground border border-border";

function flattenShows(shows: any[]) {
  return shows.flatMap((show: any) => {
    if (show.shows && show.shows.length > 0) {
      return show.shows.map((innerShow: any) => ({
        ...innerShow,
        title: formatShowLabel(show.title, innerShow.variant_name),
        status: deriveShowStatus(innerShow),
      }));
    }
    return show;
  });
}

export default function ShowCalendarGrid({
  shows,
  year,
  month,
  onMonthChange,
  onDateClick,
}: {
  shows: any[];
  year: number;
  month: number;
  onMonthChange: (year: number, month: number) => void;
  onDateClick: (dateStr: string) => void;
}) {
  const navigate = useNavigate();
  const today = dayjsTz();

  const flatShows = useMemo(() => flattenShows(shows), [shows]);

  const weeks = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = firstOfMonth.getDay();
    const cur = new Date(year, month, 1);
    cur.setDate(1 - firstWeekday);

    const showsByDay = new Map<string, any[]>();
    for (const show of flatShows) {
      if (!show.start) continue;
      const key = dayjsTz(show.start).format("YYYY-MM-DD");
      if (!showsByDay.has(key)) showsByDay.set(key, []);
      showsByDay.get(key)!.push(show);
    }

    const totalWeeks = Math.ceil(
      (firstWeekday + new Date(year, month + 1, 0).getDate()) / 7
    );

    const result = [];
    for (let w = 0; w < totalWeeks; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        const dateKey = dayjsTz(cur).format("YYYY-MM-DD");
        const inMonth = cur.getMonth() === month;
        const isToday = dateKey === today.format("YYYY-MM-DD");
        const dayShows = showsByDay.get(dateKey) ?? [];
        days.push({
          date: new Date(cur),
          dateKey,
          num: cur.getDate(),
          inMonth,
          isToday,
          shows: dayShows,
        });
        cur.setDate(cur.getDate() + 1);
      }
      result.push(days);
    }
    return result;
  }, [year, month, flatShows, today]);

  return (
    <TooltipProvider delayDuration={200}>
    <div className="flex h-full min-h-[calc(100vh-180px)] flex-1 flex-col overflow-hidden rounded-md border border-border bg-card">
      <ShowMonthNav year={year} month={month} onMonthChange={onMonthChange} />
      <div className="grid shrink-0 grid-cols-7 border-b border-border bg-muted/50">
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="flex flex-1 flex-col">
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="flex flex-1">
          {week.map((day) => {
            const visiblePills = day.shows.slice(0, MAX_PILLS_PER_DAY);
            const overflow = day.shows.length - visiblePills.length;
            const clickable = day.date >= new Date(new Date().setHours(0, 0, 0, 0));
            const isLastWeek = weekIdx === weeks.length - 1;

            return (
              <div
                key={day.dateKey}
                onClick={() => clickable && day.shows.length === 0 && onDateClick(day.dateKey)}
                className={cn(
                  "flex min-h-[78px] min-w-0 flex-1 flex-col gap-0.5 border-r border-border p-1.5 last:border-r-0",
                  !isLastWeek && "border-b",
                  day.inMonth ? "bg-card" : "bg-muted/30",
                  clickable && day.shows.length === 0 && "cursor-pointer hover:bg-muted/50"
                )}
              >
                <span
                  className={cn(
                    "flex size-[19px] flex-none items-center justify-center rounded-full text-[11px] font-semibold",
                    day.isToday
                      ? "bg-foreground text-background"
                      : day.inMonth
                        ? "text-foreground/70"
                        : "text-muted-foreground/40"
                  )}
                >
                  {day.num}
                </span>
                {visiblePills.map((show: any) => {
                  const capacity = Number(show.capacity) || 0;
                  const sold = Number(show.sold) || 0;
                  const pct =
                    capacity > 0 ? Math.min(100, Math.round((sold / capacity) * 100)) : 0;
                  const revenue = sold * (Number(show.ticket_price) || 0);

                  return (
                    <Tooltip key={show.id}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/shows/${show.id}/details`);
                          }}
                          className={cn(
                            "flex items-center gap-1 truncate rounded px-1 py-1 text-left text-[10.5px] leading-tight",
                            PILL_CLASSES
                          )}
                        >
                          <span
                            className={cn(
                              "size-2 flex-none rounded-sm",
                              SHOW_STATUS_DOT_CLASSES[show.status as ShowStatus] ??
                                SHOW_STATUS_DOT_CLASSES["on-sale"]
                            )}
                          />
                          <span className="truncate">{show.title}</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="w-64 overflow-hidden p-0">
                        <div className="flex h-36 w-full items-center justify-center bg-black">
                          <ShowThumbnail
                            src={show.img_url}
                            alt="show"
                            style={{ height: "100%", width: "100%", objectFit: "contain" }}
                          />
                        </div>

                        <div className="flex flex-col gap-1.5 p-3">
                          <div className="truncate text-[14px] font-semibold leading-tight text-foreground">
                            {show.title}
                          </div>
                          <div className="truncate text-[11.5px] text-muted-foreground">
                            {show.location_name || "No venue set"}
                          </div>

                          <div className="mt-1 flex flex-col gap-1.5 border-t border-border pt-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Status</span>
                              <StatusBadge status={show.status} />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Date</span>
                              <span className="text-[11.5px] font-medium text-foreground">
                                {dayjsTz(show.start).format("MMM D, YYYY")}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-[11px] text-muted-foreground">Sold</span>
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                                  <div
                                    className="h-full rounded-full bg-foreground"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="whitespace-nowrap text-[11.5px] font-medium text-foreground">
                                  {sold}/{capacity}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Revenue</span>
                              <span className="text-[11.5px] text-foreground">
                                {currencyFormat.format(revenue)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
                {overflow > 0 && (
                  <span className="mt-0.5 pl-0.5 text-[10.5px] font-medium text-muted-foreground/70">
                    +{overflow} more
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
      </div>
    </div>
    </TooltipProvider>
  );
}
